import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Stack,
  Grid,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Divider,
  CircularProgress
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { styled } from '@mui/material/styles';
import { useDexConfigRedux } from '../hooks/useDexConfigRedux';
import { GasSettingMode, NetworkCongestionLevel, GasPrices, GasSettingsConfig } from '../types/dex';

// 确认时间估计
const estimatedConfirmationTimes = {
  [NetworkCongestionLevel.LOW]: '< 30秒',
  [NetworkCongestionLevel.MEDIUM]: '约1分钟',
  [NetworkCongestionLevel.HIGH]: '> 2分钟'
};

// 样式组件
const GasCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
}));

const InfoIcon = styled(InfoOutlinedIcon)(({ theme }) => ({
  fontSize: '1rem',
  marginLeft: theme.spacing(0.5),
  color: theme.palette.text.secondary,
}));

const GasButton = styled(Button)(({ theme }) => ({
  minWidth: 'auto',
  fontSize: '0.75rem',
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.shape.borderRadius,
  textTransform: 'none',
  marginRight: theme.spacing(0.5),
  '&.active': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  }
}));

// 组件Props接口
interface GasSettingsProps {
  disabled?: boolean;
}

/**
 * GAS设置组件
 * 提供GAS参数调整功能，支持自动、基础和高级三种模式
 */
const GasSettings: React.FC<GasSettingsProps> = ({ disabled = false }) => {
  // 从Redux获取配置
  const { gasSettings, updatePartialGasSettingsConfig } = useDexConfigRedux();
  
  // 状态
  const [loading, setLoading] = useState<boolean>(false);
  const [gasSettingMode, setGasSettingMode] = useState<GasSettingMode>(GasSettingMode.AUTO);
  const [networkCongestionLevel, setNetworkCongestionLevel] = useState<NetworkCongestionLevel>(NetworkCongestionLevel.MEDIUM);
  const [gasPrice, setGasPrice] = useState<string>('5');
  const [maxFeePerGas, setMaxFeePerGas] = useState<string>('');
  const [maxPriorityFeePerGas, setMaxPriorityFeePerGas] = useState<string>('');
  const [gasLimit, setGasLimit] = useState<string>('300000');
  const [currentGasPrices, setCurrentGasPrices] = useState<GasPrices>({ 
    low: '3', 
    medium: '5', 
    high: '7' 
  });

  // 从Redux初始化状态
  useEffect(() => {
    if (gasSettings) {
      setGasSettingMode(gasSettings.mode || GasSettingMode.AUTO);
      setNetworkCongestionLevel(gasSettings.congestionLevel || NetworkCongestionLevel.MEDIUM);
      setGasPrice(gasSettings.gasPrice || '5');
      setMaxFeePerGas(gasSettings.maxFeePerGas || '');
      setMaxPriorityFeePerGas(gasSettings.maxPriorityFeePerGas || '');
      setGasLimit(gasSettings.gasLimit || '300000');
      if (gasSettings.currentPrices) {
        setCurrentGasPrices(gasSettings.currentPrices);
      }
    }
  }, [gasSettings]);

  // 更新Redux状态
  const updateGasSettings = () => {
    updatePartialGasSettingsConfig({
      mode: gasSettingMode,
      congestionLevel: networkCongestionLevel,
      gasPrice,
      maxFeePerGas,
      maxPriorityFeePerGas,
      gasLimit,
      currentPrices: currentGasPrices
    });
  };

  // 当状态变化时更新Redux
  useEffect(() => {
    updateGasSettings();
  }, [gasSettingMode, networkCongestionLevel, gasPrice, maxFeePerGas, maxPriorityFeePerGas, gasLimit]);

  /**
   * 获取当前Gas价格
   */
  const fetchCurrentGasPrices = async (): Promise<void> => {
    setLoading(true);
    try {
      // 模拟获取价格的延迟
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // 这里应该是从区块链获取实际价格的代码
      // 以下为模拟价格数据
      const newPrices: GasPrices = {
        low: (3 + Math.random() * 2).toFixed(1),
        medium: (5 + Math.random() * 3).toFixed(1),
        high: (8 + Math.random() * 4).toFixed(1),
        baseFeePerGas: (2 + Math.random() * 1).toFixed(1)
      };
      
      setCurrentGasPrices(newPrices);
      
      // 根据当前模式自动更新价格
      if (gasSettingMode === GasSettingMode.AUTO) {
        if (networkCongestionLevel === NetworkCongestionLevel.LOW) {
          setGasPrice(newPrices.low);
        } else if (networkCongestionLevel === NetworkCongestionLevel.MEDIUM) {
          setGasPrice(newPrices.medium);
        } else {
          setGasPrice(newPrices.high);
        }
      }
      
      if (gasSettingMode === GasSettingMode.ADVANCED && newPrices.baseFeePerGas) {
        setMaxFeePerGas((parseFloat(newPrices.baseFeePerGas) * 1.5).toFixed(1));
        setMaxPriorityFeePerGas('1.0');
      }
    } catch (error) {
      console.error('获取Gas价格失败:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 处理GAS设置模式变更
   */
  const handleGasSettingModeChange = (value: string): void => {
    setGasSettingMode(value as GasSettingMode);
  };

  /**
   * 处理网络拥堵级别变更
   */
  const handleNetworkCongestionLevelChange = (value: string): void => {
    const level = value as NetworkCongestionLevel;
    setNetworkCongestionLevel(level);
    
    // 根据拥堵级别自动设置Gas价格
    if (level === NetworkCongestionLevel.LOW) {
      setGasPrice(currentGasPrices.low);
    } else if (level === NetworkCongestionLevel.MEDIUM) {
      setGasPrice(currentGasPrices.medium);
    } else {
      setGasPrice(currentGasPrices.high);
    }
  };

  // 组件首次挂载时获取当前Gas价格
  useEffect(() => {
    fetchCurrentGasPrices();
  }, []);

  return (
    <GasCard>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
          GAS设置
        </Typography>
        <Button
          size="small"
          startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={fetchCurrentGasPrices}
          disabled={loading || disabled}
          sx={{ fontSize: '0.75rem', textTransform: 'none' }}
        >
          刷新Gas价格
        </Button>
      </Box>

      {/* 当前网络GAS价格信息 */}
      {currentGasPrices.medium && (
        <Box sx={{ mb: 1, fontSize: '0.75rem', backgroundColor: 'rgba(0, 0, 0, 0.03)', p: 1, borderRadius: 1 }}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              当前网络Gas价格: {currentGasPrices.medium} Gwei
            </Typography>
            {currentGasPrices.baseFeePerGas && (
              <Typography variant="body2" color="text.secondary">
                基础费用: {currentGasPrices.baseFeePerGas} Gwei (EIP-1559)
              </Typography>
            )}
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="success.main">慢: {currentGasPrices.low} Gwei</Typography>
              <Typography variant="caption" color="primary.main">标准: {currentGasPrices.medium} Gwei</Typography>
              <Typography variant="caption" color="error.main">快: {currentGasPrices.high} Gwei</Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
      )}

      {/* GAS设置模式选择器 */}
      <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
        <Select
          value={gasSettingMode}
          onChange={(e) => handleGasSettingModeChange(e.target.value)}
          disabled={disabled || loading}
          size="small"
        >
          <MenuItem value={GasSettingMode.AUTO}>自动设置GAS</MenuItem>
          <MenuItem value={GasSettingMode.BASIC}>基础设置</MenuItem>
          <MenuItem value={GasSettingMode.ADVANCED}>高级设置</MenuItem>
        </Select>
      </FormControl>

      {/* 自动设置模式 */}
      {gasSettingMode === GasSettingMode.AUTO && (
        <Box>
          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
            <InputLabel id="network-congestion-label" sx={{ fontSize: '0.85rem' }}>网络拥堵级别</InputLabel>
            <Select
              labelId="network-congestion-label"
              value={networkCongestionLevel}
              onChange={(e) => handleNetworkCongestionLevelChange(e.target.value)}
              disabled={disabled || loading}
              label="网络拥堵级别"
              size="small"
            >
              <MenuItem value={NetworkCongestionLevel.LOW}>低拥堵 - 慢速确认</MenuItem>
              <MenuItem value={NetworkCongestionLevel.MEDIUM}>中等拥堵 - 标准确认</MenuItem>
              <MenuItem value={NetworkCongestionLevel.HIGH}>高拥堵 - 快速确认</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 0.5 }}>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="caption">预计确认时间:</Typography>
              <Typography variant="caption" fontWeight="bold">
                {networkCongestionLevel === NetworkCongestionLevel.LOW && '~5-10 分钟'}
                {networkCongestionLevel === NetworkCongestionLevel.MEDIUM && '~1-3 分钟'}
                {networkCongestionLevel === NetworkCongestionLevel.HIGH && '~30 秒内'}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="caption">当前Gas价格:</Typography>
              <Typography variant="caption" fontWeight="bold">
                {networkCongestionLevel === NetworkCongestionLevel.LOW && `${currentGasPrices.low} Gwei`}
                {networkCongestionLevel === NetworkCongestionLevel.MEDIUM && `${currentGasPrices.medium} Gwei`}
                {networkCongestionLevel === NetworkCongestionLevel.HIGH && `${currentGasPrices.high} Gwei`}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* 基础设置模式 */}
      {gasSettingMode === GasSettingMode.BASIC && (
        <Box>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ fontWeight: 500 }}>Gas价格（Gwei）</Typography>
              <TextField
                fullWidth
                type="number"
                value={gasPrice}
                onChange={(e) => setGasPrice(e.target.value)}
                inputProps={{ min: "1", max: "100", step: "0.1" }}
                disabled={disabled || loading}
                size="small"
                sx={{ mb: 1 }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                <GasButton
                  variant="outlined"
                  size="small"
                  className={gasPrice === currentGasPrices.low ? 'active' : ''}
                  onClick={() => setGasPrice(currentGasPrices.low)}
                  disabled={disabled || loading}
                >
                  慢速 ({currentGasPrices.low})
                </GasButton>
                <GasButton
                  variant="outlined"
                  size="small"
                  className={gasPrice === currentGasPrices.medium ? 'active' : ''}
                  onClick={() => setGasPrice(currentGasPrices.medium)}
                  disabled={disabled || loading}
                >
                  标准 ({currentGasPrices.medium})
                </GasButton>
                <GasButton
                  variant="outlined"
                  size="small"
                  className={gasPrice === currentGasPrices.high ? 'active' : ''}
                  onClick={() => setGasPrice(currentGasPrices.high)}
                  disabled={disabled || loading}
                >
                  快速 ({currentGasPrices.high})
                </GasButton>
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ fontWeight: 500 }}>Gas限制</Typography>
              <TextField
                fullWidth
                type="number"
                value={gasLimit}
                onChange={(e) => setGasLimit(e.target.value)}
                inputProps={{ min: "80000", max: "1000000" }}
                disabled={disabled || loading}
                size="small"
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* 高级设置模式 */}
      {gasSettingMode === GasSettingMode.ADVANCED && (
        <Box>
          <Grid container spacing={1}>
            {currentGasPrices.baseFeePerGas ? (
              <>
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>最大Gas费用（Gwei）</Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={maxFeePerGas}
                    onChange={(e) => setMaxFeePerGas(e.target.value)}
                    inputProps={{ min: "1", max: "500", step: "0.1" }}
                    disabled={disabled || loading}
                    size="small"
                    sx={{ mb: 1 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>最大Gas优先费用（Gwei）</Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={maxPriorityFeePerGas}
                    onChange={(e) => setMaxPriorityFeePerGas(e.target.value)}
                    inputProps={{ min: "0.1", max: "10", step: "0.1" }}
                    disabled={disabled || loading}
                    size="small"
                    sx={{ mb: 1 }}
                  />
                </Grid>
              </>
            ) : (
              <Grid item xs={12}>
                <Typography variant="caption" sx={{ fontWeight: 500 }}>Gas价格（Gwei）</Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={gasPrice}
                  onChange={(e) => setGasPrice(e.target.value)}
                  inputProps={{ min: "1", max: "100", step: "0.1" }}
                  disabled={disabled || loading}
                  size="small"
                  sx={{ mb: 1 }}
                />
              </Grid>
            )}
            
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ fontWeight: 500 }}>Gas限制</Typography>
              <TextField
                fullWidth
                type="number"
                value={gasLimit}
                onChange={(e) => setGasLimit(e.target.value)}
                inputProps={{ min: "80000", max: "1000000" }}
                disabled={disabled || loading}
                size="small"
                sx={{ mb: 1 }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                backgroundColor: 'rgba(0, 0, 0, 0.03)',
                p: 0.5,
                borderRadius: 1,
                fontSize: '0.75rem'
              }}>
                <Typography variant="caption">预计最大Gas费用:</Typography>
                <Typography variant="caption" fontWeight="bold">
                  {currentGasPrices.baseFeePerGas 
                    ? `~${(parseFloat(maxFeePerGas || "0") * parseInt(gasLimit || "0") / 1e9).toFixed(6)} BNB`
                    : `~${(parseFloat(gasPrice || "0") * parseInt(gasLimit || "0") / 1e9).toFixed(6)} BNB`
                  }
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}
    </GasCard>
  );
};

export default GasSettings;
